import React, { useState, useEffect, useRef } from 'react';
import { Lock, Eye, EyeOff, Building2, FileText, Plus, Pencil, Trash2, IndianRupee, Globe, Tag, Check, X, Upload, Image, Code, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartnerSettings = () => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Logo State
  const [logo, setLogo] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Bank Details State
  const [bankDetails, setBankDetails] = useState(null);
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [bankForm, setBankForm] = useState({
    accountHolderName: '',
    accountNo1: '',
    accountNo2: '',
    ifscCode: '',
    accountType: '',
  });
  const [bankFormErrors, setBankFormErrors] = useState({});

  // GST Details State
  const [gstDetails, setGstDetails] = useState(null);
  const [isGstModalOpen, setIsGstModalOpen] = useState(false);
  const [gstForm, setGstForm] = useState({
    gstNo: '',
    panCardNo: '',
  });

  // Will Price State
  const [willPrice, setWillPrice] = useState(null);
  const [renewalPrice, setRenewalPrice] = useState(null);
  const [isWillPriceModalOpen, setIsWillPriceModalOpen] = useState(false);
  const [willPriceForm, setWillPriceForm] = useState('');
  const [renewalPriceForm, setRenewalPriceForm] = useState('');

  // Subdomain State
  const [subdomain, setSubdomain] = useState(null);
  const [isSubdomainModalOpen, setIsSubdomainModalOpen] = useState(false);
  const [subdomainForm, setSubdomainForm] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  // Embed Domain State
  const [embedDomain, setEmbedDomain] = useState(null);
  const [embedPageUrl, setEmbedPageUrl] = useState('');
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [embedDomainForm, setEmbedDomainForm] = useState('');
  const [embedPageUrlForm, setEmbedPageUrlForm] = useState('');

  // Discount Coupons State (multiple, max 5)
  const [discountCoupons, setDiscountCoupons] = useState([]);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [editingCouponCode, setEditingCouponCode] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    discountAmount: '',
  });

  const partnerToken = localStorage.getItem('partnerToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${partnerToken}` }
  };

  // Fetch existing details
  const fetchDetails = async () => {
    try {
      const response = await axios.get(`${API}/partner/details`, axiosConfig);
      if (response.data.bank_details) {
        setBankDetails(response.data.bank_details);
      }
      if (response.data.gst_details) {
        setGstDetails(response.data.gst_details);
      }
      if (response.data.will_price !== undefined && response.data.will_price !== null) {
        setWillPrice(response.data.will_price);
      }
      if (response.data.renewal_price !== undefined && response.data.renewal_price !== null) {
        setRenewalPrice(response.data.renewal_price);
      }
      if (response.data.subdomain) {
        setSubdomain(response.data.subdomain);
      }
      if (response.data.discount_coupons && response.data.discount_coupons.length > 0) {
        setDiscountCoupons(response.data.discount_coupons);
      } else if (response.data.discount_coupon) {
        setDiscountCoupons([response.data.discount_coupon]);
      }
      if (response.data.logo) {
        setLogo(response.data.logo);
      }
    } catch (error) {
      console.error('Failed to fetch details:', error);
    }
  };

  // Fetch embed settings
  const fetchEmbedSettings = async () => {
    try {
      const response = await axios.get(`${API}/partner/embed-settings`, axiosConfig);
      if (response.data.embed_domain) {
        setEmbedDomain(response.data.embed_domain);
      }
      if (response.data.embed_page_url) {
        setEmbedPageUrl(response.data.embed_page_url);
      }
    } catch (error) {
      console.error('Failed to fetch embed settings:', error);
    }
  };

  useEffect(() => {
    fetchDetails();
    fetchEmbedSettings();
  }, []);

  // ========== Logo Upload Handlers ==========
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image size should be less than 2MB');
      return;
    }

    setIsUploadingLogo(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload file
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const logoUrl = uploadResponse.data.url;

      // Save logo URL to partner
      await axios.put(`${API}/partner/logo`, { logo: logoUrl }, axiosConfig);

      setLogo(logoUrl);
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the logo?')) return;

    try {
      await axios.delete(`${API}/partner/logo`, axiosConfig);
      setLogo(null);
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await axios.put(`${API}/partner/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
      }, axiosConfig);
      
      toast.success('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== Will Price Handlers ==========
  const openWillPriceModal = () => {
    setWillPriceForm(willPrice ? willPrice.toString() : '');
    setRenewalPriceForm(renewalPrice ? renewalPrice.toString() : '');
    setIsWillPriceModalOpen(true);
  };

  const handleWillPriceSubmit = async (e) => {
    e.preventDefault();
    const price = parseFloat(willPriceForm);
    const renewal = parseFloat(renewalPriceForm);
    
    if (isNaN(price) || price < 0) {
      toast.error('Please enter a valid will price');
      return;
    }

    if (renewalPriceForm && (isNaN(renewal) || renewal < 0)) {
      toast.error('Please enter a valid renewal price');
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/partner/will-price`, { 
        price,
        renewal_price: renewalPriceForm ? renewal : null
      }, axiosConfig);
      toast.success('Pricing saved successfully!');
      setWillPrice(price);
      setRenewalPrice(renewalPriceForm ? renewal : null);
      setIsWillPriceModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save pricing';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== Subdomain Handlers ==========
  const openSubdomainModal = () => {
    setSubdomainForm(subdomain || '');
    setSubdomainAvailable(subdomain ? true : null);
    setIsSubdomainModalOpen(true);
  };

  const checkSubdomainAvailability = async (value) => {
    if (!value || value.length < 3) {
      setSubdomainAvailable(null);
      return;
    }

    setCheckingSubdomain(true);
    try {
      const response = await axios.get(`${API}/partner/check-subdomain`, {
        ...axiosConfig,
        params: { subdomain: value }
      });
      setSubdomainAvailable(response.data.available);
    } catch (error) {
      setSubdomainAvailable(false);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleSubdomainChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setSubdomainForm(value);
    setSubdomainAvailable(null);
    
    // Debounce check
    clearTimeout(window.subdomainTimeout);
    window.subdomainTimeout = setTimeout(() => {
      checkSubdomainAvailability(value);
    }, 500);
  };

  const handleSubdomainSubmit = async (e) => {
    e.preventDefault();
    
    if (!subdomainForm || subdomainForm.length < 3) {
      toast.error('Subdomain must be at least 3 characters');
      return;
    }

    if (!subdomainAvailable) {
      toast.error('This subdomain is not available');
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/partner/subdomain`, { subdomain: subdomainForm }, axiosConfig);
      toast.success('Website subdomain saved successfully!');
      setSubdomain(subdomainForm);
      setIsSubdomainModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save subdomain';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== Embed Domain Handlers ==========
  const openEmbedModal = () => {
    setEmbedDomainForm(embedDomain || '');
    setEmbedPageUrlForm(embedPageUrl || '');
    setIsEmbedModalOpen(true);
  };

  const handleEmbedSubmit = async (e) => {
    e.preventDefault();
    
    const domain = embedDomainForm.trim().toLowerCase();
    
    if (!domain) {
      toast.error('Please enter a valid domain');
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/partner/embed-settings`, {
        embed_domain: domain,
        embed_page_url: embedPageUrlForm.trim() || null
      }, axiosConfig);
      toast.success('Embed settings saved successfully!');
      setEmbedDomain(domain);
      setEmbedPageUrl(embedPageUrlForm.trim());
      setIsEmbedModalOpen(false);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save embed domain';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEmbed = async () => {
    if (!window.confirm('Are you sure you want to remove embed domain configuration?')) return;
    
    try {
      await axios.delete(`${API}/partner/embed-settings`, axiosConfig);
      toast.success('Embed domain removed');
      setEmbedDomain(null);
    } catch (error) {
      toast.error('Failed to remove embed domain');
    }
  };

  // ========== Discount Coupon Handlers ==========
  const openCouponModal = (editCode = null) => {
    if (editCode) {
      const coupon = discountCoupons.find(c => c.code === editCode);
      if (coupon) {
        setCouponForm({
          code: coupon.code,
          discountAmount: coupon.discountAmount.toString(),
        });
        setEditingCouponCode(editCode);
      }
    } else {
      setCouponForm({ code: '', discountAmount: '' });
      setEditingCouponCode(null);
    }
    setIsCouponModalOpen(true);
  };

  const handleCouponFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'code') {
      setCouponForm((prev) => ({ ...prev, [name]: value.toUpperCase().replace(/[^A-Z0-9]/g, '') }));
    } else {
      setCouponForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    
    if (!couponForm.code || couponForm.code.length < 3) {
      toast.error('Coupon code must be at least 3 characters');
      return;
    }

    const amount = parseFloat(couponForm.discountAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid discount amount');
      return;
    }

    setIsLoading(true);
    try {
      if (editingCouponCode) {
        const response = await axios.put(`${API}/partner/discount-coupon/${editingCouponCode}`, {
          code: couponForm.code,
          discountAmount: amount
        }, axiosConfig);
        setDiscountCoupons(response.data.coupons);
        toast.success('Coupon updated successfully!');
      } else {
        const response = await axios.put(`${API}/partner/discount-coupon`, {
          code: couponForm.code,
          discountAmount: amount
        }, axiosConfig);
        setDiscountCoupons(response.data.coupons);
        toast.success('Coupon added successfully!');
      }
      setIsCouponModalOpen(false);
      setEditingCouponCode(null);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save coupon';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm(`Are you sure you want to delete coupon "${code}"?`)) return;
    try {
      const response = await axios.delete(`${API}/partner/discount-coupon/${code}`, axiosConfig);
      setDiscountCoupons(response.data.coupons);
      toast.success('Coupon deleted');
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  // ========== Bank Details Handlers ==========
  const handleBankFormChange = (e) => {
    const { name, value } = e.target;
    setBankForm((prev) => ({ ...prev, [name]: value }));
    if (bankFormErrors[name]) {
      setBankFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleAccountTypeChange = (value) => {
    setBankForm((prev) => ({ ...prev, accountType: value }));
  };

  const openBankModal = (editMode = false) => {
    if (editMode && bankDetails) {
      setBankForm({
        accountHolderName: bankDetails.accountHolderName,
        accountNo1: bankDetails.accountNo,
        accountNo2: bankDetails.accountNo,
        ifscCode: bankDetails.ifscCode,
        accountType: bankDetails.accountType,
      });
    } else {
      setBankForm({
        accountHolderName: '',
        accountNo1: '',
        accountNo2: '',
        ifscCode: '',
        accountType: '',
      });
    }
    setBankFormErrors({});
    setIsBankModalOpen(true);
  };

  const validateBankForm = () => {
    const errors = {};
    if (!bankForm.accountHolderName.trim()) errors.accountHolderName = 'Name is required';
    if (!bankForm.accountNo1.trim()) errors.accountNo1 = 'Account number is required';
    if (!bankForm.accountNo2.trim()) errors.accountNo2 = 'Please confirm account number';
    if (bankForm.accountNo1 !== bankForm.accountNo2) errors.accountNo2 = 'Account numbers do not match';
    if (!bankForm.ifscCode.trim()) errors.ifscCode = 'IFSC code is required';
    if (!bankForm.accountType) errors.accountType = 'Account type is required';
    
    setBankFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    if (!validateBankForm()) return;

    setIsLoading(true);
    try {
      await axios.put(`${API}/partner/bank-details`, {
        accountHolderName: bankForm.accountHolderName,
        accountNo: bankForm.accountNo1,
        ifscCode: bankForm.ifscCode,
        accountType: bankForm.accountType,
      }, axiosConfig);
      
      toast.success('Bank details saved successfully!');
      setIsBankModalOpen(false);
      fetchDetails();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save bank details';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteBank = async () => {
    if (!window.confirm('Are you sure you want to delete bank details?')) return;
    
    try {
      await axios.delete(`${API}/partner/bank-details`, axiosConfig);
      toast.success('Bank details deleted');
      setBankDetails(null);
    } catch (error) {
      toast.error('Failed to delete bank details');
    }
  };

  const maskAccountNumber = (accountNo) => {
    if (!accountNo || accountNo.length <= 4) return accountNo;
    return '*'.repeat(accountNo.length - 4) + accountNo.slice(-4);
  };

  // ========== GST Details Handlers ==========
  const handleGstFormChange = (e) => {
    const { name, value } = e.target;
    setGstForm((prev) => ({ ...prev, [name]: value.toUpperCase() }));
  };

  const openGstModal = (editMode = false) => {
    if (editMode && gstDetails) {
      setGstForm({
        gstNo: gstDetails.gstNo,
        panCardNo: gstDetails.panCardNo,
      });
    } else {
      setGstForm({ gstNo: '', panCardNo: '' });
    }
    setIsGstModalOpen(true);
  };

  const handleGstSubmit = async (e) => {
    e.preventDefault();
    
    if (!gstForm.gstNo.trim() || !gstForm.panCardNo.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`${API}/partner/gst-details`, gstForm, axiosConfig);
      toast.success('GST details saved successfully!');
      setIsGstModalOpen(false);
      fetchDetails();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save GST details';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteGst = async () => {
    if (!window.confirm('Are you sure you want to delete GST details?')) return;
    
    try {
      await axios.delete(`${API}/partner/gst-details`, axiosConfig);
      toast.success('GST details deleted');
      setGstDetails(null);
    } catch (error) {
      toast.error('Failed to delete GST details');
    }
  };

  return (
    <div className="space-y-8" data-testid="partner-settings">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Settings</h1>
        <p className="text-slate-400 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Upload Logo */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white flex items-center gap-3">
            <div className="p-2 rounded-lg bg-pink-500/10">
              <Image className="w-5 h-5 text-pink-400" />
            </div>
            Upload Logo
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              {logo ? (
                <div className="relative group">
                  <img
                    src={logo.startsWith('http') ? logo : `${BACKEND_URL}${logo}`}
                    alt="Partner Logo"
                    className="w-32 h-32 object-contain rounded-xl border border-slate-700 bg-slate-800/50"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      className="border-slate-500 text-white hover:bg-slate-700"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleDeleteLogo}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => logoInputRef.current?.click()}
                  className="w-32 h-32 border-2 border-dashed border-slate-700 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-amber-500/50 hover:bg-slate-800/30 transition-all"
                >
                  {isUploadingLogo ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-slate-500 mb-2" />
                      <span className="text-slate-500 text-xs">Upload Logo</span>
                    </>
                  )}
                </div>
              )}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-upload-input"
              />
            </div>

            {/* Logo Info */}
            <div className="flex-1">
              <h4 className="text-white font-medium mb-2">Your Business Logo</h4>
              <p className="text-slate-400 text-sm mb-4">
                This logo will be displayed on your client login page. Recommended size: 200x200px. Max file size: 2MB.
              </p>
              {!logo && (
                <Button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="upload-logo-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {isUploadingLogo ? 'Uploading...' : 'Choose File'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Will Price */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <IndianRupee className="w-5 h-5 text-emerald-400" />
              </div>
              Pricing
            </CardTitle>
            <Button
              onClick={openWillPriceModal}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="set-will-price-btn"
            >
              {willPrice !== null ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {willPrice !== null ? 'Edit Pricing' : 'Set Pricing'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {willPrice !== null ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm">Will Price (New)</p>
                <p className="text-3xl font-bold text-emerald-400 mt-1">₹{willPrice.toLocaleString()}</p>
                <p className="text-slate-500 text-sm mt-2">Price for creating a new will</p>
              </div>
              <div className="p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm">Renewal Price</p>
                <p className="text-3xl font-bold text-amber-400 mt-1">
                  {renewalPrice !== null ? `₹${renewalPrice.toLocaleString()}` : 'Not set'}
                </p>
                <p className="text-slate-500 text-sm mt-2">Price to renew expired will access</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <IndianRupee className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No pricing set yet</p>
              <p className="text-slate-500 text-sm mt-1">Set the price for will creation and renewal</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Website Subdomain */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              Website Name
            </CardTitle>
            <Button
              onClick={openSubdomainModal}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="set-subdomain-btn"
            >
              {subdomain ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {subdomain ? 'Edit Subdomain' : 'Set Subdomain'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {subdomain ? (
            <div className="flex items-center gap-4">
              <div className="p-4 bg-slate-800/30 rounded-xl flex-1">
                <p className="text-slate-400 text-sm">Your Website URL</p>
                <p className="text-xl font-bold text-blue-400 mt-1">
                  {subdomain}<span className="text-slate-500">.willkart.ai</span>
                </p>
                <p className="text-slate-500 text-sm mt-2">Share this URL with your clients for them to login and create wills</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Globe className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No subdomain set yet</p>
              <p className="text-slate-500 text-sm mt-1">Choose a unique subdomain for your clients to access</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Discount Coupons */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Tag className="w-5 h-5 text-purple-400" />
              </div>
              Discount Coupons
              <span className="text-sm font-normal text-slate-400">
                ({discountCoupons.length}/5)
              </span>
            </CardTitle>
            {discountCoupons.length < 5 && (
              <Button
                onClick={() => openCouponModal(null)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="add-coupon-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Coupon
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {discountCoupons.length > 0 ? (
            <div className="space-y-3">
              {discountCoupons.map((coupon, idx) => (
                <div key={coupon.code} className="flex items-center justify-between p-4 bg-slate-800/30 rounded-xl" data-testid={`coupon-item-${idx}`}>
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-slate-400 text-xs">Code</p>
                      <p className="text-lg font-bold text-purple-400 font-mono">{coupon.code}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Discount</p>
                      <p className="text-lg font-bold text-purple-400">{'\u20B9'}{coupon.discountAmount?.toLocaleString()} OFF</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openCouponModal(coupon.code)}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                      data-testid={`edit-coupon-${idx}-btn`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteCoupon(coupon.code)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-coupon-${idx}-btn`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Tag className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No discount coupons created yet</p>
              <p className="text-slate-500 text-sm mt-1">Create coupon codes that your clients can use for a discount (up to 5)</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Iframe Embedding */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-cyan-500/10">
                <Code className="w-5 h-5 text-cyan-400" />
              </div>
              Iframe Embedding
            </CardTitle>
            <Button
              onClick={openEmbedModal}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="set-embed-btn"
            >
              {embedDomain ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {embedDomain ? 'Edit Domain' : 'Configure'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {embedDomain ? (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/30 rounded-xl">
                <p className="text-slate-400 text-sm">Allowed Embed Domain</p>
                <p className="text-xl font-bold text-cyan-400 mt-1">{embedDomain}</p>
                <p className="text-slate-500 text-sm mt-2">Only this domain can embed your client portal via iframe</p>
              </div>
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                <p className="text-cyan-300 text-sm">
                  <strong>How it works:</strong> Your unique embed code is available on the Dashboard. 
                  Copy and paste it into your website's HTML to display the client portal. 
                  The iframe will only work from the domain specified above.
                </p>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleDeleteEmbed}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="delete-embed-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Remove Configuration
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Code className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Iframe embedding not configured</p>
              <p className="text-slate-500 text-sm mt-1">Configure a domain to embed the client portal on your website</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Lock className="w-5 h-5 text-amber-400" />
            </div>
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handlePasswordSubmit} className="space-y-5 max-w-md" data-testid="password-form">
            <div>
              <Label htmlFor="currentPassword" className="text-slate-300 mb-2 block">
                Current Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  required
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                  className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="newPassword" className="text-slate-300 mb-2 block">
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  required
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter new password"
                  className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="text-slate-300 mb-2 block">
                Confirm New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  placeholder="Confirm new password"
                  className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="change-password-btn"
            >
              {isLoading ? 'Updating...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Bank Details */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Building2 className="w-5 h-5 text-amber-400" />
              </div>
              Bank Details
            </CardTitle>
            {!bankDetails && (
              <Button
                onClick={() => openBankModal(false)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="add-bank-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Bank
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {bankDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">Account Holder Name</p>
                  <p className="text-white font-medium mt-1">{bankDetails.accountHolderName}</p>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">Account Number</p>
                  <p className="text-white font-medium mt-1">{maskAccountNumber(bankDetails.accountNo)}</p>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">IFSC Code</p>
                  <p className="text-white font-medium mt-1">{bankDetails.ifscCode}</p>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">Account Type</p>
                  <p className="text-white font-medium mt-1 capitalize">{bankDetails.accountType}</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => openBankModal(true)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="edit-bank-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteBank}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="delete-bank-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No bank details added yet</p>
              <p className="text-slate-500 text-sm mt-1">Click "Add Bank" to add your bank account</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* GST Details */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <FileText className="w-5 h-5 text-amber-400" />
              </div>
              GST Details
            </CardTitle>
            {!gstDetails && (
              <Button
                onClick={() => openGstModal(false)}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="add-gst-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add GST Details
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {gstDetails ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">GST Number</p>
                  <p className="text-white font-medium mt-1">{gstDetails.gstNo}</p>
                </div>
                <div className="p-4 bg-slate-800/30 rounded-xl">
                  <p className="text-slate-400 text-sm">PAN Card Number</p>
                  <p className="text-white font-medium mt-1">{gstDetails.panCardNo}</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => openGstModal(true)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  data-testid="edit-gst-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteGst}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  data-testid="delete-gst-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No GST details added yet</p>
              <p className="text-slate-500 text-sm mt-1">Click "Add GST Details" to add your GST information</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Will Price Modal */}
      <Dialog open={isWillPriceModalOpen} onOpenChange={setIsWillPriceModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Set Pricing</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleWillPriceSubmit} className="space-y-5 mt-4" data-testid="will-price-form">
            <div>
              <Label htmlFor="willPrice" className="text-slate-300 mb-2 block">
                Will Price (in ₹) <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="willPrice"
                  type="number"
                  min="0"
                  step="1"
                  required
                  value={willPriceForm}
                  onChange={(e) => setWillPriceForm(e.target.value)}
                  placeholder="Enter price for new will"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="will-price-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-2">Price for clients to create a new will</p>
            </div>
            
            <div>
              <Label htmlFor="renewalPrice" className="text-slate-300 mb-2 block">
                Renewal Price (in ₹)
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="renewalPrice"
                  type="number"
                  min="0"
                  step="1"
                  value={renewalPriceForm}
                  onChange={(e) => setRenewalPriceForm(e.target.value)}
                  placeholder="Enter renewal price"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="renewal-price-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-2">Price for clients to renew expired will access (leave empty to use full price)</p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsWillPriceModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="will-price-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save Price'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subdomain Modal */}
      <Dialog open={isSubdomainModalOpen} onOpenChange={setIsSubdomainModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Set Website Subdomain</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubdomainSubmit} className="space-y-5 mt-4" data-testid="subdomain-form">
            <div>
              <Label htmlFor="subdomain" className="text-slate-300 mb-2 block">
                Choose your subdomain
              </Label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="subdomain"
                    type="text"
                    required
                    value={subdomainForm}
                    onChange={handleSubdomainChange}
                    placeholder="yourname"
                    className="pl-10 pr-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 lowercase"
                    data-testid="subdomain-input"
                  />
                  {checkingSubdomain && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-400"></div>
                    </div>
                  )}
                  {!checkingSubdomain && subdomainAvailable === true && (
                    <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-emerald-400" />
                  )}
                  {!checkingSubdomain && subdomainAvailable === false && (
                    <X className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" />
                  )}
                </div>
                <span className="text-slate-400">.willkart.ai</span>
              </div>
              {subdomainForm && subdomainForm.length >= 3 && (
                <p className={`text-sm mt-2 ${subdomainAvailable === true ? 'text-emerald-400' : subdomainAvailable === false ? 'text-red-400' : 'text-slate-500'}`}>
                  {checkingSubdomain ? 'Checking availability...' : subdomainAvailable === true ? 'This subdomain is available!' : subdomainAvailable === false ? 'This subdomain is already taken' : ''}
                </p>
              )}
              <p className="text-slate-500 text-sm mt-2">Only lowercase letters, numbers, and hyphens allowed. Minimum 3 characters.</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsSubdomainModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !subdomainAvailable}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold disabled:opacity-50"
                data-testid="subdomain-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save Subdomain'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Discount Coupon Modal */}
      <Dialog open={isCouponModalOpen} onOpenChange={setIsCouponModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {editingCouponCode ? 'Edit Discount Coupon' : 'Add Discount Coupon'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCouponSubmit} className="space-y-5 mt-4" data-testid="coupon-form">
            <div>
              <Label htmlFor="couponCode" className="text-slate-300 mb-2 block">
                Coupon Code
              </Label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="couponCode"
                  name="code"
                  type="text"
                  required
                  value={couponForm.code}
                  onChange={handleCouponFormChange}
                  placeholder="e.g., WELCOME50"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 uppercase font-mono"
                  data-testid="coupon-code-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-1">Only letters and numbers allowed</p>
            </div>
            <div>
              <Label htmlFor="discountAmount" className="text-slate-300 mb-2 block">
                Discount Amount (in ₹)
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="discountAmount"
                  name="discountAmount"
                  type="number"
                  min="1"
                  step="1"
                  required
                  value={couponForm.discountAmount}
                  onChange={handleCouponFormChange}
                  placeholder="e.g., 500"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="coupon-amount-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-1">This amount will be deducted from the will price</p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCouponModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="coupon-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save Coupon'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bank Details Modal */}
      <Dialog open={isBankModalOpen} onOpenChange={setIsBankModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {bankDetails ? 'Edit Bank Details' : 'Add Bank Details'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBankSubmit} className="space-y-5 mt-4" data-testid="bank-form">
            <div>
              <Label htmlFor="accountHolderName" className="text-slate-300 mb-2 block">
                Account Holder Name
              </Label>
              <Input
                id="accountHolderName"
                name="accountHolderName"
                type="text"
                required
                value={bankForm.accountHolderName}
                onChange={handleBankFormChange}
                placeholder="Enter account holder name"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="bank-name-input"
              />
              {bankFormErrors.accountHolderName && (
                <p className="text-red-400 text-sm mt-1">{bankFormErrors.accountHolderName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="accountNo1" className="text-slate-300 mb-2 block">
                Account Number
              </Label>
              <Input
                id="accountNo1"
                name="accountNo1"
                type="password"
                required
                value={bankForm.accountNo1}
                onChange={handleBankFormChange}
                placeholder="Enter account number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="bank-account1-input"
              />
              {bankFormErrors.accountNo1 && (
                <p className="text-red-400 text-sm mt-1">{bankFormErrors.accountNo1}</p>
              )}
            </div>
            <div>
              <Label htmlFor="accountNo2" className="text-slate-300 mb-2 block">
                Confirm Account Number
              </Label>
              <Input
                id="accountNo2"
                name="accountNo2"
                type="text"
                required
                value={bankForm.accountNo2}
                onChange={handleBankFormChange}
                placeholder="Re-enter account number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                data-testid="bank-account2-input"
              />
              {bankFormErrors.accountNo2 && (
                <p className="text-red-400 text-sm mt-1">{bankFormErrors.accountNo2}</p>
              )}
            </div>
            <div>
              <Label htmlFor="ifscCode" className="text-slate-300 mb-2 block">
                IFSC Code
              </Label>
              <Input
                id="ifscCode"
                name="ifscCode"
                type="text"
                required
                value={bankForm.ifscCode}
                onChange={handleBankFormChange}
                placeholder="Enter IFSC code"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 uppercase"
                data-testid="bank-ifsc-input"
              />
              {bankFormErrors.ifscCode && (
                <p className="text-red-400 text-sm mt-1">{bankFormErrors.ifscCode}</p>
              )}
            </div>
            <div>
              <Label htmlFor="accountType" className="text-slate-300 mb-2 block">
                Type of Account
              </Label>
              <Select value={bankForm.accountType} onValueChange={handleAccountTypeChange}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="bank-type-select">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="savings" className="text-white hover:bg-slate-800">Savings</SelectItem>
                  <SelectItem value="current" className="text-white hover:bg-slate-800">Current</SelectItem>
                </SelectContent>
              </Select>
              {bankFormErrors.accountType && (
                <p className="text-red-400 text-sm mt-1">{bankFormErrors.accountType}</p>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsBankModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="bank-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save Bank Details'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* GST Details Modal */}
      <Dialog open={isGstModalOpen} onOpenChange={setIsGstModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {gstDetails ? 'Edit GST Details' : 'Add GST Details'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGstSubmit} className="space-y-5 mt-4" data-testid="gst-form">
            <div>
              <Label htmlFor="gstNo" className="text-slate-300 mb-2 block">
                GST Number
              </Label>
              <Input
                id="gstNo"
                name="gstNo"
                type="text"
                required
                value={gstForm.gstNo}
                onChange={handleGstFormChange}
                placeholder="Enter GST number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 uppercase"
                data-testid="gst-number-input"
              />
            </div>
            <div>
              <Label htmlFor="panCardNo" className="text-slate-300 mb-2 block">
                PAN Card Number
              </Label>
              <Input
                id="panCardNo"
                name="panCardNo"
                type="text"
                required
                value={gstForm.panCardNo}
                onChange={handleGstFormChange}
                placeholder="Enter PAN card number"
                className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500 uppercase"
                data-testid="pan-number-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsGstModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="gst-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save GST Details'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Embed Domain Modal */}
      <Dialog open={isEmbedModalOpen} onOpenChange={setIsEmbedModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Configure Embed Settings</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEmbedSubmit} className="space-y-5 mt-4" data-testid="embed-form">
            <div>
              <Label htmlFor="embedDomain" className="text-slate-300 mb-2 block">
                Authorised Domain
              </Label>
              <div className="relative">
                <Code className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="embedDomain"
                  type="text"
                  required
                  value={embedDomainForm}
                  onChange={(e) => setEmbedDomainForm(e.target.value.toLowerCase())}
                  placeholder="www.example.com"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="embed-domain-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-2">
                Only this domain will be allowed to load your iframe.
              </p>
            </div>
            <div>
              <Label htmlFor="embedPageUrl" className="text-slate-300 mb-2 block">
                Page URL where iframe is embedded
                <span className="text-slate-500 font-normal ml-1">(optional)</span>
              </Label>
              <div className="relative">
                <ExternalLink className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="embedPageUrl"
                  type="text"
                  value={embedPageUrlForm}
                  onChange={(e) => setEmbedPageUrlForm(e.target.value)}
                  placeholder="https://www.example.com/will"
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-amber-500"
                  data-testid="embed-page-url-input"
                />
              </div>
              <p className="text-slate-500 text-sm mt-2">
                This link will be included in client emails as the login button. Include the full URL with path (e.g., https://www.example.com/will).
              </p>
            </div>
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
              <p className="text-cyan-300 text-sm">
                <strong>Security Note:</strong> The iframe embed will only work from the authorised domain. 
                The page URL is used for client email links only.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEmbedModalOpen(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                data-testid="embed-submit-btn"
              >
                {isLoading ? 'Saving...' : 'Save Settings'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerSettings;
